import notification from "antd/es/notification";
import browserLocale from "browser-locale";
import { uniq } from "lodash-es";
import { defineMessages } from "react-intl";
import { Channel } from "redux-saga";
import {
  actionChannel,
  all,
  call,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { Action, getType } from "typesafe-actions";

import { OrganizationSetting } from "@mapmycustomers/shared";
import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { GeocodeLimits, type GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import RawFile from "@mapmycustomers/shared/types/File";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import Permission from "@mapmycustomers/shared/types/Permission";
import PersistentLastUsedLayoutData from "@mapmycustomers/shared/types/persistent/PersistentLastUsedLayoutData";
import PersistentMapSettings from "@mapmycustomers/shared/types/persistent/PersistentMapSettings";
import Plan from "@mapmycustomers/shared/types/Plan";
import Role from "@mapmycustomers/shared/types/Role";
import Sandbox from "@mapmycustomers/shared/types/Sandbox";
import Setting from "@mapmycustomers/shared/types/Setting";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import geoService, {
  convertCoordinatesToGeoPoint,
} from "@mapmycustomers/shared/util/geo/GeoService";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import UserSetting from "@app/enum/UserSetting";
import { callApi } from "@app/store/api/callApi";
import authService from "@app/store/auth/AuthService";
import { handleError } from "@app/store/errors/actions";
import { changeLocale } from "@app/store/locale/actions";
import { fetchTeams } from "@app/store/members/actions";
import Iam from "@app/types/Iam";
import analyticsService from "@app/util/analytic/AnalyticsService";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import tripFieldModel from "@app/util/fieldModel/tripFieldModel";
import tripAmountsFieldFilter from "@app/util/fieldModel/util/tripAmountsFieldFilter";
import getLayoutModelByEntityType from "@app/util/layout/impl";
import LayoutModel from "@app/util/layout/LayoutModel";
import loggingService from "@app/util/logging";

import {
  addFavoriteSavedFilter,
  addOrgFavoriteSavedFilter,
  changePassword,
  deleteProfilePhoto,
  fetchGeocodingLimit,
  fetchMe,
  fetchOrgSandbox,
  fetchPermissions,
  fetchPlans,
  fetchUsageLimits,
  findMe,
  getUserCountry,
  removeFavoriteSavedFilters,
  removeOrgFavoriteSavedFilter,
  resetFlowStatuses,
  setCumulPromotionalUiVisited,
  togglePinnedDashboard,
  updateDefaultColumns,
  updateFieldInsightsMetaData,
  updateFlowStatus,
  updateLastUsedLayout,
  updateMe,
  updateMetadata,
  updateOrganization,
  updateOrganizationMetadata,
  updateOrganizationSetting,
  updatePreferredMapStyle,
  updateSetting,
  uploadProfilePhoto,
} from "./actions";
import {
  getCurrentUser,
  getCurrentUserId,
  getFavoriteSavedFilterIds,
  getLastUsedLayout,
  getMe,
  getMileageTrackingAmountsVisibility,
  getOrganization,
  getOrganizationId,
  getOrganizationSettingValue,
  getPinnedDashboardIds,
  getPosition,
  getRole,
  getUserSettingValue,
} from "./selectors";

const messages = defineMessages({
  passwordChangeSuccessfullyMessage: {
    id: "iam.changePassword.success",
    defaultMessage: "Password Successfully Updated",
    description: "Notification to show when user password is successfully updated",
  },
});

export function* onFetchMe() {
  try {
    const me: Iam = yield callApi("fetchMe");
    yield put(fetchMe.success(me));
  } catch (error) {
    yield put(fetchMe.failure(error));
    // error is handled in the onInitializeApp saga
  }
}

export function* onFetchPermissions({ payload }: ReturnType<typeof fetchPermissions.request>) {
  try {
    const permissions: ListResponse<Permission> = yield callApi("fetchPermissions", payload, {
      params: { $limit: 1000 },
    });
    yield put(fetchPermissions.success(permissions.data));
  } catch {
    yield put(fetchPermissions.failure());
  }
}

export function* onFindMe() {
  try {
    const position: GeolocationPosition = yield call(geoService.getCurrentPosition);
    yield put(findMe.success(position));
  } catch {
    yield put(findMe.failure());
  }
}

export function* onGetUserCountry() {
  try {
    const position: GeolocationPosition | undefined = yield select(getPosition);
    if (!position) {
      yield put(getUserCountry.failure());
      return;
    }

    const point: GeoPoint = yield call(convertCoordinatesToGeoPoint, position.coords);
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const result: GeocodeResult = yield callApi("reverseGeocodeAddress", orgId, point);

    if (result.status === "OK" && result.address.countryCode) {
      yield put(getUserCountry.success(result.address.countryCode));
    } else {
      yield put(getUserCountry.failure());
    }
  } catch {
    yield put(getUserCountry.failure());
  }
}

export function* onChangePassword({ payload }: ReturnType<typeof changePassword.request>) {
  try {
    const changedPassword: void = yield callApi(
      "changePassword",
      payload.password,
      payload.currentPassword
    );
    yield put(changePassword.success(changedPassword));
    analyticsService.completed("Change password");
    notification.success({
      message: i18nService.getIntl()?.formatMessage(messages.passwordChangeSuccessfullyMessage),
    });
  } catch (error) {
    // check error and show it either in the change pws modal (changePassword.failure) or globally (handleError)
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(changePassword.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onUploadProfilePhoto({
  payload: file,
}: ReturnType<typeof uploadProfilePhoto.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const uploadedFile: RawFile = yield callApi("createFile", organization.id, file, true);
    const currentUser: User = yield select(getCurrentUser);
    yield callApi("updateMe", {
      id: currentUser.id,
      profilePhoto: uploadedFile,
    });
    // due to a platform bug, profile photo is not updated in the response of updateMe api call
    // thus we have to call fetchMe
    const updatedProfile: Iam = yield callApi("fetchMe");
    yield put(uploadProfilePhoto.success(updatedProfile));
  } catch (error) {
    yield put(uploadProfilePhoto.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteProfilePhoto() {
  try {
    const currentUser: User = yield select(getCurrentUser);
    yield callApi("updateMe", {
      id: currentUser.id,
      profilePhoto: null,
    });
    // due to a platform bug, profile photo is not updated in the response of updateMe api call
    // thus we have to call fetchMe
    const updatedProfile: Iam = yield callApi("fetchMe");
    yield put(deleteProfilePhoto.success(updatedProfile));
  } catch (error) {
    yield put(deleteProfilePhoto.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateMe({ payload: { callback, me } }: ReturnType<typeof updateMe.request>) {
  try {
    yield callApi("updateMe", me);
    const updatedProfile: Iam = yield callApi("fetchMe");
    if (callback) {
      yield call(callback, updatedProfile);
    }
    yield put(fetchTeams.request());
    yield put(updateMe.success(updatedProfile));
  } catch (error) {
    yield put(updateMe.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateOrganization({
  payload: { callback, organization, settings },
}: ReturnType<typeof updateOrganization.request>) {
  try {
    yield callApi("updateOrganization", organization);
    if (settings && settings.length > 0) {
      yield all(
        settings.map((setting) => callApi("updateOrganizationSetting", organization.id, setting))
      );
      const currencySetting = settings.find(({ key }) => key === OrganizationSetting.CURRENCY);
      if (currencySetting) {
        localSettings.setCurrencyCode(currencySetting.value ?? "USD");
      }

      const distanceSetting = settings.find(({ key }) => key === OrganizationSetting.DISTANCE_UNIT);
      if (distanceSetting) {
        const userDistanceUnit: DistanceUnit | undefined = yield select(
          getUserSettingValue,
          UserSetting.DISTANCE_UNIT
        );
        const effectiveDistanceUnit = userDistanceUnit ?? distanceSetting.value;
        localSettings.setSiUnitsUsage(effectiveDistanceUnit === DistanceUnit.KM);
      }

      const mileageTrackingVisibilitySetting = settings.find(
        ({ key }) => key === OrganizationSetting.MILEAGE_TRACKING_VISIBILITY
      );
      if (mileageTrackingVisibilitySetting) {
        const role: Role = yield select(getRole);
        const mileageTrackingValuesVisible: boolean = getMileageTrackingAmountsVisibility(
          mileageTrackingVisibilitySetting.value,
          role
        );
        if (mileageTrackingValuesVisible) {
          tripFieldModel.removeFieldFilter(tripAmountsFieldFilter);
        } else {
          tripFieldModel.addFieldFilter(tripAmountsFieldFilter);
        }
      }
    }
    const updatedProfile: Iam = yield callApi("fetchMe");
    if (callback) {
      yield call(callback);
    }
    yield put(updateOrganization.success(updatedProfile));
  } catch (error) {
    yield put(updateOrganization.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateOrganizationSetting({
  payload: { callback, setting },
}: ReturnType<typeof updateOrganizationSetting.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("updateOrganizationSetting", orgId, setting);
    if (setting.key === OrganizationSetting.CURRENCY) {
      localSettings.setCurrencyCode(setting.value ?? "USD");
    }
    const updatedProfile: Iam = yield callApi("fetchMe");
    if (callback) {
      yield call(callback);
    }
    yield put(updateOrganizationSetting.success(updatedProfile));
  } catch (error) {
    yield put(updateOrganizationSetting.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateSetting({
  payload: { callback, orgId, setting, userId },
}: ReturnType<typeof updateSetting.request>) {
  try {
    const updatedSetting: Setting = yield callApi("updateUserSetting", orgId, userId, setting);
    callback?.(updatedSetting);
    yield put(updateSetting.success(updatedSetting));

    if (setting.key === "language") {
      const locale = setting.value ? String(setting.value) : browserLocale();
      yield put(changeLocale.request(locale));
    }

    if (setting.key === UserSetting.DISTANCE_UNIT) {
      const orgDistanceUnit: DistanceUnit | undefined = yield select(
        getOrganizationSettingValue,
        OrganizationSetting.DISTANCE_UNIT
      );
      const effectiveDistanceUnit =
        (setting.value as DistanceUnit | null) ?? orgDistanceUnit ?? DistanceUnit.MILE;
      localSettings.setSiUnitsUsage(effectiveDistanceUnit === DistanceUnit.KM);
    }
  } catch (error) {
    yield put(updateSetting.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateMetadata({ payload }: ReturnType<typeof updateMetadata.request>) {
  try {
    const user: User = yield select(getCurrentUser);
    const updatedUser: Iam = yield callApi("updateMe", {
      id: user.id,
      metaData: payload,
    });
    yield put(updateMetadata.success(updatedUser.metaData));
  } catch (error) {
    yield put(updateMetadata.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateOrganizationMetadata({
  payload,
}: ReturnType<typeof updateOrganizationMetadata.request>) {
  try {
    const { callback, metaData } = payload;
    const organization: Organization = yield select(getOrganization);
    const updatedOrganization: Organization = yield callApi("updateOrganization", {
      ...organization,
      metaData: { ...(organization.metaData ?? {}), ...metaData },
    });
    if (callback) {
      yield call(callback, updatedOrganization.metaData);
    }
    yield put(updateOrganizationMetadata.success(updatedOrganization));
  } catch (error) {
    yield put(updateOrganizationMetadata.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onAddFavoriteSavedFilter({
  payload,
}: ReturnType<typeof addFavoriteSavedFilter.request>) {
  try {
    const user: User = yield select(getCurrentUser);
    const favoriteFilters: SavedFilter["id"][] = yield select(getFavoriteSavedFilterIds);
    if (!favoriteFilters.includes(payload)) {
      const updatedUser: User = yield callApi("updateMe", {
        id: user.id,
        metaData: { quickFilters: [...favoriteFilters, payload] },
      });
      yield put(addFavoriteSavedFilter.success(updatedUser));
    }
  } catch (error) {
    yield put(addFavoriteSavedFilter.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveFavoriteFilters({
  payload,
}: ReturnType<typeof removeFavoriteSavedFilters.request>) {
  try {
    const user: User = yield select(getCurrentUser);
    const favoriteFilters: SavedFilter["id"][] = yield select(getFavoriteSavedFilterIds);
    const quickFilters = favoriteFilters.filter((filterId) => !payload.includes(filterId));

    if (quickFilters.length !== user.metaData.quickFilters?.length) {
      const updatedUser: User = yield callApi("updateMe", {
        id: user.id,
        metaData: { quickFilters },
      });
      yield put(removeFavoriteSavedFilters.success(updatedUser));
    } else {
      yield put(removeFavoriteSavedFilters.success(user));
    }
  } catch (error) {
    yield put(addFavoriteSavedFilter.failure());
    yield put(handleError({ error }));
  }
}

export function* onAddOrgFavoriteSavedFilter({
  payload,
}: ReturnType<typeof addOrgFavoriteSavedFilter.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const favorites = organization.metaData?.quickFilters ?? [];
    // Like the remove saga below, skip a save that wouldn't change the list: a CS support
    // session can't save the org, so it would only fail
    if (favorites.includes(payload)) {
      return;
    }
    const quickFilters = uniq([...favorites, payload]);
    const updatedOrganization: Organization = yield callApi("updateOrganization", {
      ...organization,
      metaData: { ...(organization.metaData ?? {}), quickFilters },
    });
    yield put(addOrgFavoriteSavedFilter.success(updatedOrganization));
  } catch (error) {
    yield put(addOrgFavoriteSavedFilter.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveOrgFavoriteSavedFilter({
  payload,
}: ReturnType<typeof removeOrgFavoriteSavedFilter.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const favorites = organization.metaData?.quickFilters ?? [];
    if (!favorites.includes(payload)) {
      return;
    }
    const quickFilters = favorites.filter((filterId) => filterId !== payload);
    const updatedOrganization: Organization = yield callApi("updateOrganization", {
      ...organization,
      metaData: { ...(organization.metaData ?? {}), quickFilters },
    });
    yield put(removeOrgFavoriteSavedFilter.success(updatedOrganization));
  } catch (error) {
    yield put(removeOrgFavoriteSavedFilter.failure());
    yield put(handleError({ error }));
  }
}

export function* onSetCumulPromotionalUIVisited() {
  try {
    const user: User = yield select(getCurrentUser);
    const updatedUser: User = yield callApi("updateMe", {
      id: user.id,
      metaData: { ...user.metaData, isCumulDemoVisited: true },
    });

    yield put(setCumulPromotionalUiVisited.success(updatedUser));
  } catch (error) {
    yield put(setCumulPromotionalUiVisited.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onTogglePinnedDashboard({
  payload,
}: ReturnType<typeof togglePinnedDashboard.request>) {
  try {
    const user: User = yield select(getCurrentUser);
    const pinnedDashboardIds: CumulDashboard["id"][] = yield select(getPinnedDashboardIds);
    let updatedPinnedDashboards = [];
    const isDashboardAlreadyPinned = pinnedDashboardIds.includes(payload);
    if (isDashboardAlreadyPinned) {
      updatedPinnedDashboards = pinnedDashboardIds.filter((dashboard) => dashboard !== payload);
    } else {
      updatedPinnedDashboards = [...pinnedDashboardIds, payload];
    }
    const updatedUser: User = yield callApi("updateMe", {
      id: user.id,
      metaData: { pinnedDashboardIds: updatedPinnedDashboards },
    });
    yield put(togglePinnedDashboard.success(updatedUser));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(togglePinnedDashboard.failure(error));
  }
}

export function* onFetchGeocodingLimit() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const limits: GeocodeLimits = yield callApi("fetchGeocodeLimits", orgId);
    yield put(
      fetchGeocodingLimit.success({
        geocodingMmcLimitReached: limits.MMC.limit <= limits.MMC.used,
        geocodingOrgLimit: limits.organization.limit,
        geocodingOrgLimitReached: limits.organization.limit <= limits.organization.used,
      })
    );
  } catch (error) {
    yield put(fetchGeocodingLimit.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateLastUsedLayout({ payload }: ReturnType<typeof updateLastUsedLayout>) {
  try {
    const { entityType, layoutId } = payload;
    const lastUsedLayout: PersistentLastUsedLayoutData = yield select(getLastUsedLayout);
    yield put(
      updateMetadata.request({ lastUsedLayout: { ...lastUsedLayout, [entityType]: layoutId } })
    );
    const layoutModel: LayoutModel<typeof entityType> = yield call(
      getLayoutModelByEntityType,
      entityType
    );
    layoutModel.setRecentLayoutId(layoutId);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUpdateDefaultColumns({ payload }: ReturnType<typeof updateDefaultColumns>) {
  try {
    const { callback, defaultColumns } = payload;
    yield put(
      updateOrganizationMetadata.request({
        callback: () => {
          callback?.();
        },
        metaData: { defaultColumns },
      })
    );
    if (!defaultColumns) {
      return;
    }

    Object.keys(defaultColumns).forEach((entityType) => {
      const fieldModel = getFieldModelByEntityType(
        entityType as EntityTypesSupportedColumnSelection
      );
      if (!fieldModel) {
        loggingService.debug("Invalid entity type in default columns", entityType);
        return;
      }

      const defaultFields =
        defaultColumns[
          entityType as keyof Exclude<OrganizationMetaData["defaultColumns"], undefined>
        ]
          ?.map((platformFieldName) => fieldModel.getByPlatformName(platformFieldName))
          .filter(isDefined)
          .map((field) => field.name) ?? [];

      if (defaultFields.length) {
        fieldModel.fields.forEach((field) => {
          field.toggleFeature(
            FieldFeature.VISIBLE_BY_DEFAULT,
            field.hasFeature(FieldFeature.ALWAYS_VISIBLE) || defaultFields.includes(field.name)
          );
        });
      }
    });
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUpdateFlowStatus({ payload }: ReturnType<typeof updateFlowStatus.request>) {
  try {
    const { slug, status } = payload;
    const user: User = yield select(getCurrentUser);
    const updatedUser: Iam = yield callApi("updateMe", {
      id: user.id,
      metaData: { flowStatuses: { ...user.metaData.flowStatuses, [slug]: status } },
    });
    yield put(updateFlowStatus.success(updatedUser.metaData));
  } catch (error) {
    yield put(updateFlowStatus.failure());
    yield put(handleError({ error }));
  }
}

export function* onResetFlowStatuses() {
  try {
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    const updatedUser: Iam = yield callApi("updateMe", {
      id: currentUserId,
      metaData: { flowStatuses: {} },
    });
    yield put(resetFlowStatuses.success(updatedUser.metaData));
  } catch (error) {
    yield put(resetFlowStatuses.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdatePreferredMapStyle({
  payload: style,
}: ReturnType<typeof updatePreferredMapStyle>) {
  const me: Iam | undefined = yield select(getMe);
  const mapSettings: Partial<PersistentMapSettings> | undefined = me?.metaData?.mapSettings;
  yield put(updateMetadata.request({ mapSettings: { ...mapSettings, style } }));
}

export function* onUpdateFieldInsightsMetaData({
  payload,
}: ReturnType<typeof updateFieldInsightsMetaData>) {
  const { callback, failureCallback, metaData } = payload;
  try {
    const user: User = yield select(getCurrentUser);
    const changedFieldInsights = Object.fromEntries(
      Object.entries(metaData).map(([key, value]) => [key, value ?? null])
    );
    const updatedUser: Iam = yield callApi("patchMeMetaData", user.id, {
      fieldInsights: changedFieldInsights,
    });
    yield put(updateMetadata.success(updatedUser.metaData));
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(updateMetadata.failure(error));
    yield put(handleError({ error }));
    if (failureCallback) {
      yield call(failureCallback);
    }
  }
}

export function* onFetchPlans() {
  try {
    const response: ListResponse<Plan> = yield callApi("fetchPlans");
    yield put(fetchPlans.success(response.data));
  } catch (error) {
    yield put(fetchPlans.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchUsageLimits() {
  try {
    const orgId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (orgId) {
      const response: Iam["usageLimits"] = yield callApi("fetchUsageLimits", orgId);
      yield put(fetchUsageLimits.success(response));
    }
  } catch {
    yield put(fetchUsageLimits.failure());
  }
}

function* fetchUsageLimitsPeriodically() {
  while (true) {
    // only fetch when token is defined
    if (authService.hasToken()) {
      yield put(fetchUsageLimits.request());
    }
    yield delay(60000);
  }
}

// Ensure metadata updates are processed sequentially to avoid
// any race conditions that could lead to inconsistent state
function* watchUpdateMetadata() {
  const channel: Channel<Action> = yield actionChannel([
    getType(updateMetadata.request),
    getType(updateFieldInsightsMetaData),
  ]);
  while (true) {
    const action: Action = yield take(channel);
    if (action.type === getType(updateFieldInsightsMetaData)) {
      yield call(
        onUpdateFieldInsightsMetaData,
        action as ReturnType<typeof updateFieldInsightsMetaData>
      );
    } else {
      yield call(onUpdateMetadata, action as ReturnType<typeof updateMetadata.request>);
    }
  }
}

export function* onFetchOrgSandbox() {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    // the server lists no private rows to the web app and keeps one non-private row per org
    const response: ListResponse<Sandbox> = yield callApi("fetchSandboxes", organizationId);
    yield put(fetchOrgSandbox.success(response.data[0]));
  } catch (error) {
    // no toast for a background read; the sandbox settings page reports the failure itself
    yield put(fetchOrgSandbox.failure(error));
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* iamSaga() {
  yield takeLatest(fetchMe.request, onFetchMe);
  yield takeLatest(fetchPermissions.request, onFetchPermissions);
  // the Sandbox page polls every 2s while a sandbox clones or deletes; cancelling a slower read would
  // keep its status from ever updating
  yield takeLeading(fetchOrgSandbox.request, onFetchOrgSandbox);
  yield takeLatest(findMe.request, onFindMe);
  yield takeLatest(getUserCountry.request, onGetUserCountry);
  yield takeLatest(changePassword.request, onChangePassword);
  yield takeLatest(uploadProfilePhoto.request, onUploadProfilePhoto);
  yield takeLatest(deleteProfilePhoto.request, onDeleteProfilePhoto);
  yield takeLatest(updateSetting.request, onUpdateSetting);
  // Serialize updateMetadata.request (and updateFieldInsightsMetaData) through a
  // queue. Concurrent partial-payload writes (e.g. routeViewSettings +
  // boundariesSettings dispatched in the same tick) would otherwise race
  // server-side: each handler reads, merges, writes back, and the later one
  // clobbers the earlier one. Processing one at a time ensures each PUT sees the
  // previous response merged in.
  yield fork(watchUpdateMetadata);
  yield takeLatest(updateOrganizationMetadata.request, onUpdateOrganizationMetadata);
  yield takeLatest(addFavoriteSavedFilter.request, onAddFavoriteSavedFilter);
  yield takeLatest(addOrgFavoriteSavedFilter.request, onAddOrgFavoriteSavedFilter);
  yield takeLatest(removeOrgFavoriteSavedFilter.request, onRemoveOrgFavoriteSavedFilter);
  yield takeLatest(togglePinnedDashboard.request, onTogglePinnedDashboard);
  yield takeLatest(setCumulPromotionalUiVisited.request, onSetCumulPromotionalUIVisited);
  yield takeLatest(removeFavoriteSavedFilters.request, onRemoveFavoriteFilters);
  yield takeLatest(updateMe.request, onUpdateMe);
  yield takeLatest(updateOrganization.request, onUpdateOrganization);
  yield takeLatest(updateOrganizationSetting.request, onUpdateOrganizationSetting);
  yield takeLatest(fetchGeocodingLimit.request, onFetchGeocodingLimit);
  yield takeLatest(updateLastUsedLayout, onUpdateLastUsedLayout);
  yield takeLatest(updateDefaultColumns, onUpdateDefaultColumns);
  yield takeLatest(updateFlowStatus.request, onUpdateFlowStatus);
  yield takeLatest(resetFlowStatuses.request, onResetFlowStatuses);
  yield takeLatest(fetchPlans.request, onFetchPlans);
  yield takeLatest(fetchUsageLimits.request, onFetchUsageLimits);
  yield takeLatest(updatePreferredMapStyle, onUpdatePreferredMapStyle);
  yield fork(fetchUsageLimitsPeriodically);
}
